import {Box, Grid} from '@mui/material';
import Container from '@mui/material/Container';
import {useCallback, useEffect, useState} from 'react';
import Button from '@mui/material/Button';
import PageHeadline from '../../components/PageHeadline';
import {useTranslation} from 'react-i18next';
import {useParams} from 'react-router';
import AccessControl, {UserPermissions} from '../../components/shared/AccessControl';
import {FeatureName} from '../../../paths';
import {PageStickyHeader} from '../PageStickyHeader';
import {PageTopActions} from '../PageTopActions';
import ConfirmationDialog from '../../components/dialogs/ConfirmationDialog';
import {FormBuilder} from '../../form/FormBuilder';
import {useFormAPI} from '../../form/FormAPI';
import {usePermissions, UsePermissionState} from '../UsePermissions';
import {BillingAccount, BillingAccountListItem} from '../../model/BillingAccount';
import {onRejectSubmit} from '../../form/errorHandler';
import {OnSubmitHandler} from '../../form/model';
import {FormState} from '../../form/state/FormState';
import {TestAttributes} from '../../TestAttributes';
import NotificationService, {NotificationType} from '../../services/NotificationService';
import {useNavigate} from 'react-router-dom';
import {difference} from '../../form/utils';
import {Skeleton} from '@mui/lab';
import {ResponseListWrapper} from '../../services/model/ResponseListWrapper';
import {useAxiosContext} from '../../context/AxiosContext';
import {useLoading} from '../../context/LoadingContext';

function BillingAccountPage() {
  const {id} = useParams();
  const {t} = useTranslation();
  const {setLoading} = useLoading();
  const {useAxiosAccountsAPI} = useAxiosContext();
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState<boolean>(false);
  const {userPermissions}: UsePermissionState = usePermissions(FeatureName.BILLING_ACCOUNTS);
  const [billingAccount, setBillingAccount] = useState<BillingAccount | null>(null);
  const formAPI = useFormAPI();

  const navigate = useNavigate();

  const [, getEntityList] = useAxiosAccountsAPI<ResponseListWrapper<BillingAccountListItem>>(
    {url: 'admin/accounts'},
    {
      manual: true,
      autoCancel: false,
    }
  );
  const [{data: entityData, loading: isGetEntityLoading, error: getEntityError}, getEntityData] =
    useAxiosAccountsAPI<BillingAccount>(`/admin/accounts/${id}`, {manual: true});

  interface ResourceIdResponse {
    id: string;
  }

  const [{error: patchEntityError, loading: isPatchEntityLoading, response: patchEntityResponse}, patchEntityData] =
    useAxiosAccountsAPI<ResourceIdResponse>(
      {url: `/admin/accounts/${id}`, method: 'PATCH', params: {formatted: true}},
      {manual: true}
    );

  const [{error: postEntityError, loading: isPostEntityLoading, response: postEntityResponse}, postEntityData] =
    useAxiosAccountsAPI<ResourceIdResponse>({url: `admin/accounts`, method: 'POST'}, {manual: true});

  const onSubmit: OnSubmitHandler = useCallback(
    (formData: Partial<BillingAccount>, state: FormState) => {
      if (id) {
        formData = difference(formData, billingAccount);
        patchEntityData({
          data: formData,
        }).then(() => {
          getEntityData();
        });
      } else {
        postEntityData({
          data: formData,
        }).then(() => {});
      }
    },
    [billingAccount]
  );

  useEffect(() => {
    const loading = isGetEntityLoading || isPostEntityLoading || isPatchEntityLoading;
    setLoading(loading, 'BillingAccountPage');
  }, [isGetEntityLoading, isPostEntityLoading, isPatchEntityLoading]);

  useEffect(() => {
    const entityError = patchEntityError || postEntityError || getEntityError;
    if (entityError) {
      NotificationService.getInstance().sendNotification(entityError?.response?.data?.message, NotificationType.ERROR);
    }
  }, [patchEntityError, postEntityError, getEntityError]);

  useEffect(() => {
    if (id) {
      getEntityData();
    } else {
      setBillingAccount(null);
    }
  }, [id]);

  useEffect(() => {
    if (entityData) {
      setBillingAccount(entityData);
    }
  }, [entityData]);

  useEffect(() => {
    if (postEntityResponse?.status === 201) {
      getEntityList().then(() => {
        NotificationService.getInstance().sendNotification(
          postEntityResponse.data?.['message'],
          NotificationType.SUCCESS
        );
        navigate(`/${FeatureName.BILLING_ACCOUNTS}/${postEntityResponse.data['id']}`);
      });
    }
  }, [postEntityResponse]);

  useEffect(() => {
    if (patchEntityResponse?.status === 200) {
      getEntityList().then(() => {
        NotificationService.getInstance().sendNotification(
          patchEntityResponse.data?.['message'],
          NotificationType.SUCCESS
        );
      });
    }
  }, [patchEntityResponse]);

  return (
    <Box
      sx={{
        backgroundColor: 'background.default',
        minHeight: '100%',
        py: 3,
      }}
    >
      <Container maxWidth={false}>
        <Grid container spacing={3}>
          <PageStickyHeader>
            <Grid container item xs={12} rowSpacing={{xs: 3, sm: 3}}>
              <Grid item xs={12} md={6} sx={{display: 'flex', alignItems: 'center'}}>
                <PageHeadline>{t('billing-accounts.headline')}</PageHeadline>
              </Grid>
              <Grid item xs={12} md={6}>
                <PageTopActions>
                  <Button
                    {...{[TestAttributes.BUTTON_NAME]: 'confirmation-dialog-btn'}}
                    id={'confirmation-dialog-btn'}
                    color="secondary"
                    onClick={() => setIsConfirmationDialogOpen(true)}
                  >
                    {t('shared.clear')}
                  </Button>

                  <AccessControl
                    userPermissions={userPermissions}
                    allowedPermissions={[id ? UserPermissions.MODIFY : UserPermissions.CREATE]}
                  >
                    <Button
                      {...{[TestAttributes.BUTTON_NAME]: 'save-btn'}}
                      id={'save-btn'}
                      onClick={() => {
                        formAPI.submit();
                      }}
                    >
                      {id ? t('shared.update') : t('shared.save')}
                    </Button>
                  </AccessControl>
                </PageTopActions>
              </Grid>
            </Grid>
          </PageStickyHeader>
          <Grid item xs={12}>
            {isGetEntityLoading ? (
              <Skeleton variant="rectangular" height={500} />
            ) : (
              <FormBuilder
                onRejectSubmit={onRejectSubmit}
                formId={FeatureName.BILLING_ACCOUNTS}
                api={formAPI}
                initialValues={billingAccount}
                onSubmit={onSubmit}
              />
            )}
          </Grid>
        </Grid>
        <ConfirmationDialog
          message={t('shared.clear-form-modal-content')}
          headline={t('shared.clear-form-modal-headline')}
          isDialogOpen={isConfirmationDialogOpen}
          handleClose={() => setIsConfirmationDialogOpen(false)}
        >
          <Button
            onClick={() => {
              formAPI.reset();
              setIsConfirmationDialogOpen(false);
            }}
            {...{[TestAttributes.BUTTON_NAME]: 'confirmation-dialog-action-btn'}}
            id="confirmation-dialog-action-btn"
          >
            {t('shared.accept')}
          </Button>
        </ConfirmationDialog>
      </Container>
    </Box>
  );
}

export default BillingAccountPage;
